import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Button, IconButton, Tooltip, Chip, Avatar, Typography, useTheme, alpha } from '@mui/material';
import { eventService } from '../../../services/eventService';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Assignment as AssignmentIcon, ReportProblem as PenaltySidebarIcon, Schedule as ScheduleIcon, Person as PersonIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, HourglassEmpty as HourglassEmptyIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import { format, differenceInCalendarDays, startOfDay } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import ListFilterBar from '../../common/ListFilterBar';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { PENALTY_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import { canCreatePenalty, canModifyPenalty } from '../../../utils/eventPenaltyPermissions';

const SUCCESS_GREEN = '#34D399';
const DUE_CORAL = '#F87171';
const DUE_AMBER = '#FBBF24';
const DUE_OK = '#34D399';

const getPenaltyDueDateDisplay = (penalty) => {
  if (!penalty?.due_date) {
    return { text: '—', color: 'rgba(226, 232, 240, 0.75)' };
  }
  const dueDate = new Date(penalty.due_date);
  const daysLeft = differenceInCalendarDays(startOfDay(dueDate), startOfDay(new Date()));
  const isDone = penalty.status === 'done';
  const isOverdue = !isDone && daysLeft < 0;
  const isDueSoon = !isDone && daysLeft >= 0 && daysLeft <= 3;
  const color = isOverdue
    ? DUE_CORAL
    : isDueSoon
      ? DUE_AMBER
      : isDone
        ? 'rgba(226, 232, 240, 0.75)'
        : DUE_OK;
  return { text: format(dueDate, 'MMM dd, yyyy'), color };
};

const PenaltyList = ({ eventId }) => {
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();

  const navigate = useNavigate();
  const theme = useTheme();
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedPenalty, setSelectedPenalty] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [hasEvents, setHasEvents] = useState(Boolean(eventId));
  const canEditPenalties = canCreatePenalty(user);

  const filterOptions = [{ name: 'status', label: 'Status', type: 'select', options: [{ value: 'not_started', label: 'Not Started' }, { value: 'in_progress', label: 'In Progress' }, { value: 'done', label: 'Done' }] }];

  const fetchPenalties = async () => {
    try {
      setLoading(true);
      const endpoint = eventId
        ? `/events/${eventId}/penalties?per_page=200`
        : '/penalties?per_page=200';
      const response = await axiosInstance.get(endpoint);
      const payload = response?.data;
      setPenalties(Array.isArray(payload) ? payload : (payload?.data || []));
      setError(null);
    } catch {
      setError('Failed to fetch penalties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPenalties(); }, [eventId]);

  useEffect(() => {
    if (eventId) {
      setHasEvents(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await eventService.getAll();
        if (!cancelled) setHasEvents(Array.isArray(rows) && rows.length > 0);
      } catch {
        if (!cancelled) setHasEvents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const getStatusColor = (status) => ({ not_started: 'error', in_progress: 'warning', done: 'success' }[status] || 'default');
  const getStatusIcon = (status) => ({ not_started: <CancelIcon fontSize="small" />, in_progress: <HourglassEmptyIcon fontSize="small" />, done: <CheckCircleIcon fontSize="small" /> }[status] || null);

  const filteredPenalties = penalties
    .filter((p) => {
      const matchesStatus = !filters.status || p.status === filters.status;
      const t = String(p?.title ?? '');
      const d = String(p?.description ?? '');
      const assignedName = String(p?.assigned_to?.name ?? p?.assignedTo?.name ?? '');
      const matchesSearch =
        t.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignedName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      const aCompleted = String(a.status).toLowerCase() === 'done';
      const bCompleted = String(b.status).toLowerCase() === 'done';
      if (aCompleted === bCompleted) return 0;
      return aCompleted ? 1 : -1;
    });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: AssignmentIcon, value: penalties.length, label: 'Total Penalties', color: 'info' },
    { icon: CancelIcon, value: penalties.filter((p) => p.status === 'not_started').length, label: 'Not Started', color: 'error' },
    { icon: HourglassEmptyIcon, value: penalties.filter((p) => p.status === 'in_progress').length, label: 'In Progress', color: 'warning' },
    { icon: CheckCircleIcon, value: penalties.filter((p) => p.status === 'done').length, label: 'Completed', color: 'success' },
  ];

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'not_started', 'in_progress', 'done'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'not_started' ? 1 :
    activeStatFilter === 'in_progress' ? 2 :
    activeStatFilter === 'done' ? 3 :
    0;

  const handleDelete = (penalty) => {
    setSelectedPenalty(penalty);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPenalty) return;
    // #region agent log
    fetch('http://127.0.0.1:7565/ingest/cc91522b-f5c5-4dcd-b7ad-7eaf4d98ea85',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'fd7048'},body:JSON.stringify({sessionId:'fd7048',runId:'pre-fix',hypothesisId:'D',location:'PenaltyList.jsx:confirmDelete',message:'penalty delete attempt',data:{penaltyId:selectedPenalty.id,canModify:canModifyPenalty(user,selectedPenalty),userMgmt:user?.management_id,assigneeMgmt:selectedPenalty?.assigned_to?.management_id??selectedPenalty?.assignedTo?.management_id},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    try {
      setDeleteLoading(true);
      await axiosInstance.delete(`/penalties/${selectedPenalty.id}`);
      // #region agent log
      fetch('http://127.0.0.1:7565/ingest/cc91522b-f5c5-4dcd-b7ad-7eaf4d98ea85',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'fd7048'},body:JSON.stringify({sessionId:'fd7048',runId:'post-fix',hypothesisId:'E',location:'PenaltyList.jsx:confirmDelete',message:'penalty delete success',data:{penaltyId:selectedPenalty.id},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setPenalties(penalties.filter(p => p.id !== selectedPenalty.id));
      setDeleteDialogOpen(false);
      setSelectedPenalty(null);
      setSuccess(`Penalty "${selectedPenalty.title}" has been deleted successfully!`);
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7565/ingest/cc91522b-f5c5-4dcd-b7ad-7eaf4d98ea85',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'fd7048'},body:JSON.stringify({sessionId:'fd7048',runId:'pre-fix',hypothesisId:'D',location:'PenaltyList.jsx:confirmDelete',message:'penalty delete failed',data:{penaltyId:selectedPenalty.id,status:err?.response?.status,apiMessage:err?.response?.data?.message||err?.response?.data?.error},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to delete penalty');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { 
      key: 'title', 
      label: 'Penalty', 
      icon: AssignmentIcon, 
      minWidth: 220,
      render: (p) => (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500, color: "rgba(226, 232, 240, 0.85)" }}>
            {p.title}
          </Typography>
          <Typography variant="body2" color="rgba(226, 232, 240, 0.6)">
            {p.description}
          </Typography>
        </Box>
      ) 
    },
    { 
      key: 'assigned_to', 
      label: 'Assigned To', 
      icon: PersonIcon, 
      minWidth: 180,
      render: (p) => {
        const assignedName = p?.assigned_to?.name ?? p?.assignedTo?.name ?? '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 40, height: 40 }}>
              {assignedName ? assignedName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Typography
              variant="body2"
              sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: "rgba(226, 232, 240, 0.85)" }}
            >
              {assignedName || 'Unassigned'}
            </Typography>
          </Box>
        );
      } 
    },
    { 
      key: 'status', 
      label: 'Status', 
      minWidth: 140,
      render: (p) => (
        <Chip 
          icon={getStatusIcon(p.status)} 
          label={p.status.replace('_', ' ').toUpperCase()} 
          color={getStatusColor(p.status)} 
          size="small" 
        /> 
      ) 
    },
    { 
      key: 'due_date', 
      label: 'Due Date', 
      icon: ScheduleIcon, 
      minWidth: 140,
      render: (p) => {
        const { text, color } = getPenaltyDueDateDisplay(p);
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.78rem',
              fontWeight: color === DUE_CORAL || color === DUE_AMBER ? 600 : 400,
              color,
            }}
          >
            {text}
          </Typography>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (p) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Penalty" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/penalties/${p.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canModifyPenalty(user, p) && (
            <>
              <Tooltip title="Edit Penalty" arrow>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); navigate(`/penalties/edit/${p.id}`, { state: { penalty: p } }); }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: `rgba(245, 158, 11, 0.1)`,
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Penalty" arrow>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: `rgba(239, 68, 68, 0.1)`,
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!user) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading penalties list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header 
        title="Penalty Management" 
        subtitle="Track and manage penalties assigned to team members" 
        icon={PenaltySidebarIcon} 
        actionButton={
          canEditPenalties ? (
            <Tooltip
              title={hasEvents ? '' : 'Create at least one event before adding a penalty.'}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/penalties/add')}
                  disabled={!hasEvents}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' },
                  }}
                >
                  Add Penalty
                </Button>
              </span>
            </Tooltip>
          ) : null
        } 
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS_GREEN, 0.12),
          border: `1px solid ${alpha(SUCCESS_GREEN, 0.35)}`,
          boxShadow: '0 4px 20px rgba(34,197,94,0.12)',
          color: alpha(SUCCESS_GREEN, 0.95),
          mb: 3,
        }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={(newFilters) => {
          const nextActive =
            newFilters.status === 'not_started' ? 'not_started' :
            newFilters.status === 'in_progress' ? 'in_progress' :
            newFilters.status === 'done' ? 'done' :
            'all';
          setActiveStatFilter(nextActive);
          setFilters(newFilters);
          setPage(0);
        }}
        onClearFilters={() => {
          setFilters({});
          setActiveStatFilter('all');
          setSearchTerm('');
          setPage(0);
        }}
        filterOptions={filterOptions}
        searchPlaceholder="Search by title, description, or assigned user..."
      />
      <ListBulkToolbar
        config={PENALTY_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: PENALTY_BULK_CONFIG.endpoint,
          setItems: setPenalties,
          selectedIds,
          clearSelection,
          setSuccess,
          setError,
          successLabel: 'penalty(s)',
          refetch: fetchPenalties,
          filterDeleteIds: (ids) => {
            const modifiable = ids.filter((id) => {
              const penalty = penalties.find((row) => Number(row.id) === Number(id));
              return penalty && canModifyPenalty(user, penalty);
            });
            if (!modifiable.length) {
              setError('None of the selected penalties can be deleted.');
              return false;
            }
            return modifiable;
          },
        })}
      />



      <TableList
        columns={columns}
        data={filteredPenalties}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        {...bulkTableProps(PENALTY_BULK_CONFIG, user, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <AssignmentIcon />,
          title: 'No penalties found',
          description: searchTerm || filters.status
            ? 'Try adjusting your filters to see more penalties.'
            : 'Get started by creating your first penalty.'
        }}
        onRowClick={(p) => navigate(`/penalties/${p.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedPenalty(null); }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        itemType="penalty"
        itemName={selectedPenalty?.title}
        description="This action cannot be undone and will permanently remove the penalty data."
        confirmLabel="Delete Penalty"
      />
    </Container>
  );
};

export default PenaltyList;